/**
 * 统一图标渲染入口。
 *
 * 为什么不直接用 lucide 组件：尺寸只允许四档、strokeWidth 与尺寸绑定（12px 必须 2.25）、
 * 图标默认必须 aria-hidden（与文字组合时重复朗读是最常见的可访问性回归）。
 * 把这三条编码进类型，就不可能在某个页面写出 `size={18}` 或忘记 aria 处理。
 * 规范来源：docs/06-ICON-MAP.md §1.1、design-tokens.json icon.$rules。
 */
import { resolveIcon } from './registry';
import type { IconName } from './registry';
import { ICON_SIZE, strokeWidthFor } from '../tokens';
import type { IconSize } from '../tokens';

export interface IconProps {
  readonly name: IconName;
  /** 四档之一。默认 16px（行内） */
  readonly size?: IconSize;
  /**
   * 可访问名。**只有独立承载语义的图标才传**（如纯图标按钮）。
   * 与文字并排出现时不要传 —— 图标会被 aria-hidden，由文字承担语义。
   */
  readonly label?: string;
  /** 颜色。只接受 token 派生值（cssVar(...)），默认 currentColor */
  readonly color?: string;
  readonly className?: string;
  /** 加载图标专用：仅 loader-circle 允许旋转（06 §4） */
  readonly spin?: boolean;
}

export function Icon({
  name,
  size = ICON_SIZE.inline,
  label,
  color,
  className,
  spin = false,
}: IconProps) {
  const Component = resolveIcon(name);
  const decorative = label === undefined;

  if (spin && name !== 'loader-circle') {
    // 开发期即刻暴露误用，而不是让一堆图标在生产环境转起来
    throw new Error(`只有 loader-circle 允许旋转动效（06-ICON-MAP.md §4），收到：${name}`);
  }

  const classes = [spin ? 'icon-spin' : null, className].filter(Boolean).join(' ');

  return (
    <Component
      width={size}
      height={size}
      strokeWidth={strokeWidthFor(size)}
      color={color ?? 'currentColor'}
      className={classes.length > 0 ? classes : undefined}
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : 'img'}
      aria-label={label}
      focusable="false"
      style={{ flexShrink: 0 }}
    />
  );
}
